/**
 * CallRoot: Telefonie-Root inkl. UI-Context, Modals und Panel.
 * - Nur Audio-WebRTC
 * - Online-Signaling via Socket.IO (Pfad /ws, JWT im Handshake)
 * - Offline-Modus: Manuelles Signaling (Copy/Paste von Offer/Answer, ohne Socket/Server)
 * - UI-States: idle -> calling -> ringing -> connected -> ended; incoming -> connected -> ended
 * - Ringtones: /sounds/ring.mp3 (callee), /sounds/ringback.mp3 (caller)
 * Hinweise:
 *   - Stelle sicher, dass die Audiodateien unter client/public/sounds/ vorhanden sind.
 *   - Button-Klicks (User-Gesten) starten Audio (Autoplay-Restriktionen).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSignaling } from '../../hooks/useSignaling';


// Einfache UUID für CallIDs
function uuid() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0; const v = c === 'x' ? r : (r & 0x3) | 0x8; return v.toString(16);
  });
}

// Hilfen zum (De)Serialisieren der SDP-Objekte
function encodeDesc(desc) {
  try {
    return btoa(JSON.stringify(desc));
  } catch (_) {
    return '';
  }
}
function decodeDesc(txt) {
  try {
    return JSON.parse(atob((txt || '').trim()));
  } catch (_) {
    return null;
  }
}

const ICE_SERVERS = [
  { urls: ['stun:stun.l.google.com:19302', 'stun:global.stun.twilio.com:3478'] },
  // Öffentlicher Test-TURN (nur für Tests / Demo). Für Produktion eigenen TURN (coturn) verwenden.
  { urls: ['turn:openrelay.metered.ca:80?transport=tcp'], username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: ['turn:openrelay.metered.ca:443?transport=tcp'], username: 'openrelayproject', credential: 'openrelayproject' },
  { urls: ['turns:openrelay.metered.ca:443'], username: 'openrelayproject', credential: 'openrelayproject' }
];

// useWebRTC Hook (Audio-only)
function useWebRTC(signaling, me) {
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);

  const [state, setState] = useState('idle'); // idle | calling | ringing | incoming | connected | ended
  const [callId, setCallId] = useState(null);
  const [peerUserId, setPeerUserId] = useState(null);

  const [muted, setMuted] = useState(false);
  const remoteAudioRef = useRef(null);

  // Klingeltöne
  const ringRef = useRef(null);
  const ringbackRef = useRef(null);

  // Eingehendes Offer zwischenspeichern bis Benutzer annimmt/ablehnt
  const pendingOfferRef = useRef(null);

  // ICE-Gathering vollständig abwarten (für Offline: Trickle-ICE deaktivieren)
  async function waitForIceGatheringComplete(pc) {
    if (!pc) return;
    if (pc.iceGatheringState === 'complete') return;
    await new Promise((resolve) => {
      const check = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', check);
          resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', check);
      // Fallback: nach 3s abbrechen, falls Browser "complete" nicht feuert
      setTimeout(() => {
        try { pc.removeEventListener('icegatheringstatechange', check); } catch (_) {}
        resolve();
      }, 3000);
    });
  }

  // Cleanup
  const cleanup = useCallback((reason = 'ended') => {
    try {
      if (ringRef.current) { ringRef.current.pause(); ringRef.current.currentTime = 0; }
      if (ringbackRef.current) { ringbackRef.current.pause(); ringbackRef.current.currentTime = 0; }
    } catch (_) {}

    try {
      if (pcRef.current) { pcRef.current.onicecandidate = null; pcRef.current.ontrack = null; pcRef.current.close(); }
    } catch (_) {}
    pcRef.current = null;

    try {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(t => { try { t.stop(); } catch (e) {} });
      }
    } catch (_) {}
    localStreamRef.current = null;

    remoteStreamRef.current = null;
    if (remoteAudioRef.current) {
      try { remoteAudioRef.current.srcObject = null; } catch (_) {}
    }
    pendingOfferRef.current = null;

    setMuted(false);
    setPeerUserId(null);
    setCallId(null);
    setState('ended');
    // Nach kurzer Zeit in idle zurück
    setTimeout(() => setState('idle'), 250);
  }, []);

  // RTCPeerConnection erstellen
  const ensurePC = useCallback(() => {
    if (pcRef.current) return pcRef.current;
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pc.onicecandidate = (e) => {
      // Online-Signaling: nur senden, wenn eine Gegenstelle gesetzt ist
      if (e.candidate && callId && peerUserId) {
        signaling?.sendIceCandidate({ callId, fromUserId: me.id, toUserId: peerUserId, candidate: e.candidate });
      }
    };
    pc.ontrack = (e) => {
      if (!remoteStreamRef.current) {
        remoteStreamRef.current = new MediaStream();
      }
      // Audio-Track hinzufügen
      if (e.track.kind === 'audio') {
        remoteStreamRef.current.addTrack(e.track);
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = remoteStreamRef.current;
        }
      }
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === 'failed' || s === 'disconnected' || s === 'closed') {
        cleanup('network_error');
      }
    };
    pcRef.current = pc;
    return pc;
  }, [signaling, callId, peerUserId, me?.id, cleanup]);

  // Events vom Signaling (Online-Modus)
  useEffect(() => {
    if (!signaling) return;

    const handlers = {
      onRinging: (p) => {
        // Caller Ringback starten
        try {
          ringbackRef.current = new Audio('/sounds/ringback.mp3');
          ringbackRef.current.loop = true;
          ringbackRef.current.play().catch(() => {});
        } catch (_) {}
        setState('ringing');
      },
      onBusy: (p) => {
        cleanup('busy');
      },
      onCallOffer: async ({ callId: cid, fromUserId, toUserId, sdpOffer }) => {
        // Eingehender Anruf
        setCallId(cid);
        setPeerUserId(fromUserId);
        pendingOfferRef.current = sdpOffer;
        setState('incoming');
        // Klingeln starten
        try {
          ringRef.current = new Audio('/sounds/ring.mp3');
          ringRef.current.loop = true;
          ringRef.current.play().catch(() => {});
        } catch (_) {}
      },
      onCallAnswer: async ({ callId: cid, sdpAnswer }) => {
        // Caller: Remote SDP setzen, Ringback stoppen
        if (ringbackRef.current) { try { ringbackRef.current.pause(); ringbackRef.current.currentTime = 0; } catch (_) {} }
        try {
          const pc = ensurePC();
          await pc.setRemoteDescription(sdpAnswer);
          setState('connected');
        } catch (e) {
          cleanup('network_error');
        }
      },
      onIceCandidate: async ({ candidate }) => {
        try {
          const pc = ensurePC();
          await pc.addIceCandidate(candidate);
        } catch (_) {}
      },
      onHangup: ({ reason }) => {
        cleanup(reason || 'ended');
      },
      onDecline: ({ reason }) => {
        if (ringbackRef.current) { try { ringbackRef.current.pause(); ringbackRef.current.currentTime = 0; } catch (_) {} }
        cleanup(reason || 'declined');
      },
      onError: (_e) => {
        cleanup('network_error');
      }
    };

    // Lokale Registrierung: useSignaling setzt die Handler bereits beim Erzeugen
    const unsub = signalingSubscribe(signaling, handlers);
    return () => { try { unsub && unsub(); } catch (_) {} };
  }, [signaling, ensurePC, cleanup]);

  // Helper, um Handler temporär zu binden
  function signalingSubscribe(sig, h) {
    const s = sig && sig.socket;
    if (!s) return () => {};

    const binds = [];
    const on = (ev, fn) => {
      if (typeof fn === 'function') {
        s.on(ev, fn);
        binds.push([ev, fn]);
      }
    };

    on('user-online', h.onUserOnline);
    on('user-offline', h.onUserOffline);
    on('ringing', h.onRinging);
    on('busy', h.onBusy);
    on('call-offer', h.onCallOffer);
    on('call-answer', h.onCallAnswer);
    on('ice-candidate', h.onIceCandidate);
    on('hangup', h.onHangup);
    on('decline', h.onDecline);
    on('error', h.onError);

    return () => {
      binds.forEach(([ev, fn]) => {
        try { s.off(ev, fn); } catch (_) {}
      });
    };
  }

  // Start Call (Caller) — Online
  const startCall = useCallback(async (targetUserId) => {
    if (!me?.id || !targetUserId) return;
    const cid = uuid();
    setCallId(cid);
    setPeerUserId(targetUserId);
    setState('calling');

    try {
      // Audio holen (User-Geste: Klick im Directory)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;

      const pc = ensurePC();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);

      signaling?.sendOffer({
        callId: cid,
        fromUserId: me.id,
        toUserId: targetUserId,
        sdpOffer: { type: 'offer', sdp: offer.sdp }
      });
    } catch (e) {
      cleanup('permission_denied');
    }
  }, [ensurePC, signaling, me, cleanup]);

  // Annehmen (Callee) — Online
  const acceptCall = useCallback(async () => {
    if (!me?.id || !pendingOfferRef.current || !peerUserId || !callId) return;
    try {
      if (ringRef.current) { try { ringRef.current.pause(); ringRef.current.currentTime = 0; } catch (_) {} }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;

      const pc = ensurePC();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      await pc.setRemoteDescription(pendingOfferRef.current);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      signaling?.sendAnswer({
        callId,
        fromUserId: me.id,
        toUserId: peerUserId,
        sdpAnswer: { type: 'answer', sdp: answer.sdp }
      });

      setState('connected');
    } catch (e) {
      cleanup('permission_denied');
    }
  }, [ensurePC, signaling, me, peerUserId, callId, cleanup]);

  // Ablehnen (Callee) — Online
  const declineCall = useCallback(() => {
    if (ringRef.current) { try { ringRef.current.pause(); ringRef.current.currentTime = 0; } catch (_) {} }
    if (callId) {
      signaling?.sendDecline({ callId });
    }
    cleanup('declined');
  }, [signaling, callId, cleanup]);

  // Auflegen
  const hangup = useCallback(() => {
    if (callId) {
      signaling?.sendHangup({ callId });
    }
    cleanup('ended');
  }, [signaling, callId, cleanup]);

  // Mute/Unmute (lokal)
  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      try {
        if (localStreamRef.current) {
          localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !next; });
        }
      } catch (_) {}
      return next;
    });
  }, []);

  // OFFLINE-MODUS (manuelles Signaling, kein Socket)
  const createOfflineOffer = useCallback(async () => {
    try {
      setState('calling'); // visuell: ausgehender Verbindungsaufbau
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;

      const pc = ensurePC();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      await waitForIceGatheringComplete(pc);

      const local = pc.localDescription;
      return encodeDesc(local);
    } catch (e) {
      cleanup('permission_denied');
      throw e;
    }
  }, [ensurePC, cleanup]);

  const applyOfflineAnswer = useCallback(async (encodedAnswer) => {
    try {
      const pc = ensurePC();
      const answer = decodeDesc(encodedAnswer);
      if (!answer) throw new Error('Ungültige Antwort');
      await pc.setRemoteDescription(answer);
      setState('connected');
    } catch (e) {
      cleanup('network_error');
      throw e;
    }
  }, [ensurePC, cleanup]);

  const applyOfflineOfferGenerateAnswer = useCallback(async (encodedOffer) => {
    try {
      const offer = decodeDesc(encodedOffer);
      if (!offer) throw new Error('Ungültiges Angebot');

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;

      const pc = ensurePC();
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));

      await pc.setRemoteDescription(offer);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      await waitForIceGatheringComplete(pc);

      const local = pc.localDescription;
      // Wir markieren „verbunden“, die tatsächliche ICE-Verbindung wird folgen,
      // sobald die Gegenseite die Antwort setzt.
      setState('connected');
      return encodeDesc(local);
    } catch (e) {
      cleanup('network_error');
      throw e;
    }
  }, [ensurePC, cleanup]);

  return {
    state, callId, peerUserId,
    remoteAudioRef,
    startCall, acceptCall, declineCall, hangup, toggleMute,
    muted,
    // Offline:
    createOfflineOffer,
    applyOfflineAnswer,
    applyOfflineOfferGenerateAnswer,
  };
}

// UI-Context
const CallUIContext = createContext(null);
export function useCallUI() {
  // Return context if present, otherwise null — components should handle absence gracefully.
  // This prevents uncaught exceptions when a consumer is rendered outside the provider (causes app crash).
  return useContext(CallUIContext);
}

export function CallRoot({ children }) {
  const { user, isSuperAdmin } = useAuth();
  const isAdmin = !!(user && (isSuperAdmin || String(user.role || '').toLowerCase() === 'admin'));

  const signaling = useSignaling({
    // Die Low-Level-Events werden im useWebRTC Hook verbraucht; keine direkten Handler erforderlich
  });

  const rtc = useWebRTC(signaling, user || {});

  const [directoryOpen, setDirectoryOpen] = useState(false);
  const [directoryUsers, setDirectoryUsers] = useState([]);
  const [dirQuery, setDirQuery] = useState('');

  // Offline-UI
  const [offlineOpen, setOfflineOpen] = useState(false);
  const [offlineRole, setOfflineRole] = useState('caller'); // 'caller' | 'callee'
  const [offlineOfferText, setOfflineOfferText] = useState('');
  const [offlineAnswerText, setOfflineAnswerText] = useState('');
  const [offlineInputText, setOfflineInputText] = useState(''); // Eingabe für Offer/Answer je nach Rolle
  const [offlineError, setOfflineError] = useState('');

  const openDirectory = useCallback(async () => {
    if (!isAdmin) return;
    setDirectoryOpen(true);
    try {
      const res = await api.get('/api/users/directory');
      const list = (res?.data?.users || []).filter(Boolean);
      setDirectoryUsers(list);
    } catch (e) {
      // ignore
    }
  }, [isAdmin]);

  const closeDirectory = useCallback(() => setDirectoryOpen(false), []);

  const value = useMemo(() => ({
    isAdmin,
    openDirectory,
    ...rtc,
  }), [isAdmin, openDirectory, rtc]);

  // Filter
  const filteredUsers = useMemo(() => {
    const q = (dirQuery || '').toLowerCase();
    if (!q) return directoryUsers;
    return directoryUsers.filter((u) => (u.name || '').toLowerCase().includes(q));
  }, [directoryUsers, dirQuery]);

  // Offline Actions
  const handleCreateOfflineOffer = useCallback(async () => {
    setOfflineError('');
    try {
      const enc = await rtc.createOfflineOffer();
      setOfflineOfferText(enc);
    } catch (e) {
      setOfflineError('Offer konnte nicht erzeugt werden (Mikrofonberechtigung?)');
    }
  }, [rtc]);

  const handleApplyOfflineAnswer = useCallback(async () => {
    setOfflineError('');
    try {
      await rtc.applyOfflineAnswer(offlineInputText);
      // Erfolgreich — Modal kann geschlossen werden
      setOfflineOpen(false);
      setOfflineInputText('');
    } catch (e) {
      setOfflineError('Antwort konnte nicht angewendet werden. Bitte Eingabe prüfen.');
    }
  }, [rtc, offlineInputText]);

  const handleApplyOfflineOfferGenerateAnswer = useCallback(async () => {
    setOfflineError('');
    try {
      const enc = await rtc.applyOfflineOfferGenerateAnswer(offlineInputText);
      setOfflineAnswerText(enc);
    } catch (e) {
      setOfflineError('Angebot konnte nicht angewendet werden. Bitte Eingabe prüfen.');
    }
  }, [rtc, offlineInputText]);

  const copyToClipboard = useCallback(async (txt) => {
    try {
      await navigator.clipboard.writeText(txt || '');
    } catch (_) {
      // ignore
    }
  }, []);



  return (
    <CallUIContext.Provider value={value}>
      {children}
      {/* Hidden remote audio */}
      <audio ref={rtc.remoteAudioRef} autoPlay playsInline />

      {/* Directory Modal */}
      {directoryOpen && isAdmin && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[2000]" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Справочник администраторов</h2>
              <div className="flex items-center gap-2">
                {/* Offline-Button öffnet manuelles Signaling */}
                <button
                  onClick={() => { setOfflineOpen(true); setOfflineRole('caller'); setOfflineOfferText(''); setOfflineAnswerText(''); setOfflineInputText(''); setOfflineError(''); setDirectoryOpen(false); }}
                  className="px-2 py-1 text-xs rounded bg-secondary-100 hover:bg-secondary-200"
                  title="Offline (manuelles Signaling: Copy/Paste)"
                >
                  Offline
                </button>
                <button onClick={closeDirectory} className="text-secondary-600 hover:text-secondary-900" aria-label="Закрыть">✕</button>
              </div>
            </div>
            <input
              type="text"
              className="w-full border rounded px-3 py-2 mb-3"
              placeholder="Поиск по имени..."
              value={dirQuery}
              onChange={(e) => setDirQuery(e.target.value)}
            />
            <div className="max-h-80 overflow-auto divide-y">
              {filteredUsers.filter(u => u.id !== user?.id).map((u) => (
                <div key={u.id} className="w-full px-2 py-3 hover:bg-secondary-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img src={u.avatar || '/favicon.ico'} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                    <div>
                      <div className="font-medium">{u.name}</div>
                      <div className="text-xs text-secondary-600">{u.role} {u.online ? '• в сети' : '• не в сети'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { closeDirectory(); rtc.startCall(u.id); }}
                      className="px-3 py-1 rounded bg-secondary-100 hover:bg-secondary-200 text-sm"
                    >
                      Позвонить
                    </button>
                  </div>
                </div>
              ))}
              {filteredUsers.length === 0 && (
                <div className="p-3 text-sm text-secondary-600">Подходящих пользователей не найдено.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Offline Call Modal (Manuelles Signaling) */}
      {offlineOpen && isAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2100]" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Offline-Anruf (manuelles Signaling)</h2>
              <button onClick={() => setOfflineOpen(false)} className="text-secondary-600 hover:text-secondary-900" aria-label="Schließen">✕</button>
            </div>

            <p className="text-sm text-secondary-700 mb-3">
              Kein Server erforderlich. Tauschen Sie das Angebot (Offer) und die Antwort (Answer) per Chat/QR/Text aus. Beide Seiten müssen die App geöffnet haben.
            </p>

            <div className="flex gap-3 mb-3">
              <button
                className={`px-3 py-2 rounded ${offlineRole === 'caller' ? 'bg-primary-600 text-white' : 'bg-secondary-100 hover:bg-secondary-200'}`}
                onClick={() => { setOfflineRole('caller'); setOfflineInputText(''); setOfflineAnswerText(''); }}
              >
                Ich bin Anrufer/in
              </button>
              <button
                className={`px-3 py-2 rounded ${offlineRole === 'callee' ? 'bg-primary-600 text-white' : 'bg-secondary-100 hover:bg-secondary-200'}`}
                onClick={() => { setOfflineRole('callee'); setOfflineInputText(''); setOfflineOfferText(''); }}
              >
                Ich bin Empfänger/in
              </button>
            </div>

            {offlineError && <div className="mb-3 text-sm text-red-600">{offlineError}</div>}

            {/* Caller-Flow */}
            {offlineRole === 'caller' && (
              <div className="space-y-4">
                <div className="border rounded p-3">
                  <div className="font-medium mb-2">Schritt 1: Angebot (Offer) erzeugen</div>
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      onClick={handleCreateOfflineOffer}
                      className="px-3 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
                    >
                      Angebot erzeugen
                    </button>
                    {offlineOfferText && (
                      <button
                        onClick={() => copyToClipboard(offlineOfferText)}
                        className="px-3 py-2 rounded bg-secondary-100 hover:bg-secondary-200"
                      >
                        In Zwischenablage kopieren
                      </button>
                    )}
                  </div>
                  <textarea
                    className="w-full h-28 border rounded p-2 text-xs"
                    readOnly
                    placeholder="Klicken Sie auf 'Angebot erzeugen', dann erscheint hier der Text zum Weitergeben."
                    value={offlineOfferText}
                  />
                  <div className="text-xs text-secondary-600 mt-1">
                    Senden Sie dieses Angebot an die Gegenseite (z. B. über Chat).
                  </div>
                </div>

                <div className="border rounded p-3">
                  <div className="font-medium mb-2">Schritt 2: Antwort (Answer) von Gegenseite einfügen</div>
                  <textarea
                    className="w-full h-28 border rounded p-2 text-xs"
                    placeholder="Fügen Sie hier die erhaltene Antwort (Answer) ein…"
                    value={offlineInputText}
                    onChange={(e) => setOfflineInputText(e.target.value)}
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                      onClick={handleApplyOfflineAnswer}
                      className="px-3 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
                    >
                      Antwort übernehmen
                    </button>
                    <button
                      onClick={() => { setOfflineOpen(false); }}
                      className="px-3 py-2 rounded bg-secondary-100 hover:bg-secondary-200"
                    >
                      Schließen
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Callee-Flow */}
            {offlineRole === 'callee' && (
              <div className="space-y-4">
                <div className="border rounded p-3">
                  <div className="font-medium mb-2">Schritt 1: Angebot (Offer) von Gegenseite einfügen</div>
                  <textarea
                    className="w-full h-28 border rounded p-2 text-xs"
                    placeholder="Fügen Sie hier das erhaltene Angebot (Offer) ein…"
                    value={offlineInputText}
                    onChange={(e) => setOfflineInputText(e.target.value)}
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                      onClick={handleApplyOfflineOfferGenerateAnswer}
                      className="px-3 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
                    >
                      Angebot übernehmen & Antwort erzeugen
                    </button>
                  </div>
                </div>

                <div className="border rounded p-3">
                  <div className="font-medium mb-2">Schritt 2: Antwort (Answer) an Gegenseite senden</div>
                  <div className="flex items-center gap-2 mb-2">
                    {offlineAnswerText && (
                      <button
                        onClick={() => copyToClipboard(offlineAnswerText)}
                        className="px-3 py-2 rounded bg-secondary-100 hover:bg-secondary-200"
                      >
                        In Zwischenablage kopieren
                      </button>
                    )}
                  </div>
                  <textarea
                    className="w-full h-28 border rounded p-2 text-xs"
                    readOnly
                    placeholder="Nach Schritt 1 erscheint hier Ihre Antwort zum Weitergeben."
                    value={offlineAnswerText}
                  />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button
                      onClick={() => { setOfflineOpen(false); }}
                      className="px-3 py-2 rounded bg-secondary-100 hover:bg-secondary-200"
                    >
                      Schließen
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-3 text-xs text-secondary-600">
              Hinweise:
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>WebRTC benötigt eine Netzwerkverbindung zwischen den Peers (z. B. lokales Netzwerk). „Offline“ bedeutet hier: ohne Signaling-Server.</li>
                <li>Für stabile Verbindungen in verschiedenen Netzen TURN konfigurieren (siehe docs/rtc.md). Ohne TURN können Verbindungen fehlschlagen.</li>
                <li>Browser erfragen Mikrofonzugriff beim Starten des Anrufs.</li>
              </ul>
            </div>
          </div>
        </div>
      )}



      {/* Incoming Modal (Online) */}
      {rtc.state === 'incoming' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000]" role="dialog" aria-modal="true">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-4">
            <h2 className="text-lg font-semibold mb-2">Входящий вызов</h2>
            <p className="text-sm text-secondary-700 mb-4">Вам звонит администратор/суперадминистратор.</p>
            <div className="flex items-center justify-end gap-2">
              <button onClick={rtc.declineCall} className="px-3 py-2 rounded bg-secondary-100 hover:bg-secondary-200">Отклонить</button>
              <button onClick={rtc.acceptCall} className="px-3 py-2 rounded bg-primary-600 text-white hover:bg-primary-700">Принять</button>
            </div>
          </div>
        </div>
      )}

      {/* Call Panel */}
      {rtc.state === 'connected' && (
        <div className="fixed bottom-4 right-4 z-[2000] bg-white/90 backdrop-blur-md border border-secondary-200 rounded-xl p-3 shadow-lg">
            <div className="flex items-center gap-3">
            <button onClick={rtc.toggleMute} className={`px-3 py-2 rounded ${rtc.muted ? 'bg-secondary-200' : 'bg-secondary-100'} hover:bg-secondary-200`} title={rtc.muted ? 'Включить звук' : 'Отключить звук'}>
              {rtc.muted ? '🔇' : '🎤'}
            </button>
            <button onClick={rtc.hangup} className="px-3 py-2 rounded bg-red-600 text-white hover:bg-red-700" title="Завершить вызов">📞 Завершить</button>
          </div>
        </div>
      )}
    </CallUIContext.Provider>
  );
}
